// Purchase Order-related filters

import {
	PurchaseOrderReceiptExceptionStatus,
	PurchaseOrderReceiptExceptionStatusLabels,
	PurchaseOrderReceiptExceptionTypeLabels,
	PurchaseOrderStatusLabels,
} from '@supply-mover-2-0/shared';
import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createStatusFilter,
} from './factories';
import type { FilterRegistry } from './types';

/**
 * PD-14690: the claim statuses a vendor issue can sit in while still
 * "outstanding" — everything except the terminal RESOLVED / WRITTEN_OFF,
 * which the issue-filter family's base predicate excludes outright.
 * Lifecycle order, matching the exception state machine.
 */
const OPEN_CLAIM_STATUSES: ReadonlyArray<PurchaseOrderReceiptExceptionStatus> =
	[
		PurchaseOrderReceiptExceptionStatus.OPEN,
		PurchaseOrderReceiptExceptionStatus.VENDOR_NOTIFIED,
		PurchaseOrderReceiptExceptionStatus.CREDIT_PENDING,
		PurchaseOrderReceiptExceptionStatus.CREDIT_RECEIVED,
		PurchaseOrderReceiptExceptionStatus.REPLACEMENT_PENDING,
		PurchaseOrderReceiptExceptionStatus.REPLACEMENT_RECEIVED,
	];

export const purchaseOrderFilters: FilterRegistry = {
	purchaseOrderKeywords: {
		...createKeywordFilter({
			label: 'Search',
			// externalId is the only text-searchable column on purchase_order —
			// listing non-existent columns (externalId2/description/notes, copied
			// from another entity's filter) made every keyword search fail with a
			// SQL error (PD-14565). Vendor-name matching needs relation-aware
			// search support and is tracked as follow-up scope on that ticket.
			placeholder: 'Search by PO number...',
			objectTable: 'purchase_order',
			listKeys: ['purchase_order', 'purchase-order-vendor-embedded'],
			fulltextFields: ['externalId'],
		}),
		queryParamName: 'purchaseOrderKeywords',
	},

	purchaseOrderStatus: {
		...createStatusFilter({
			label: 'Status',
			// PD-14595: derive from the shared label map so filter chips
			// and status badges can never disagree (numeric-like keys
			// enumerate in ascending code order).
			options: Object.entries(PurchaseOrderStatusLabels).map(([id, label]) => ({
				id,
				label,
			})),
			objectTable: 'purchase_order',
			fieldName: 'status',
			listKeys: ['purchase_order', 'purchase-order-vendor-embedded'],
		}),
		queryParamName: 'purchaseOrderStatus',
	},

	purchaseOrderVendor: {
		...createEntityLookupFilter({
			label: 'Vendor',
			placeholder: 'Search vendors...',
			apiEndpoint: '/vendors',
			objectTable: 'purchase_order',
			// Legacy underscore-prefixed FK column on purchase_order. The
			// receipt table's equivalent is `fk_vendor__id` (no prefix); copying
			// that name here would 500 the list (the PD-14565 class of bug), so
			// use the real column, `_fk_vendor__id`.
			fieldName: '_fk_vendor__id',
			listKeys: ['purchase_order'],
			displayFields: {
				id: 'id',
				primary: 'name',
			},
			colorScheme: 'blue',
			searchable: true,
		}),
		queryParamName: 'purchaseOrderVendor',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	purchaseOrderExpirationDateRange: {
		...createDateRangeFilter({
			label: 'Expiration Date',
			objectTable: 'purchase_order',
			fieldName: 'expirationDate',
			listKeys: ['purchase_order'],
		}),
		queryParamName: 'purchaseOrderExpirationDateRange',
	},

	purchaseOrderFollowUpDateRange: {
		...createDateRangeFilter({
			label: 'Follow-Up Date',
			objectTable: 'purchase_order',
			fieldName: 'followUpDate',
			listKeys: ['purchase_order'],
		}),
		queryParamName: 'purchaseOrderFollowUpDateRange',
	},

	// PD-14666: does the PO carry unresolved receipt exceptions? Drives
	// the "Outstanding Vendor Issues" tab (locked to Yes) and doubles as
	// a user-facing sidebar filter. Reads the denormalized
	// `openExceptionCount` column, which the exception recompute path
	// keeps NULL-when-zero precisely so this can be the registry's
	// IS-NOT-NULL kind (joinType 'exists' + fieldName) instead of a
	// multi-hop EXISTS subquery the builder can't express.
	purchaseOrderHasOpenIssues: {
		type: 'single-select',
		label: 'Open Vendor Issues',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'purchaseOrderHasOpenIssues',
		multiple: false,
		valueExtractor: {
			property: 'id',
		},
		options: [
			{ id: 'true', label: 'Yes' },
			{ id: 'false', label: 'No' },
		],
		queryMapping: {
			objectTable: 'purchase_order',
			fieldName: 'openExceptionCount',
			joinType: 'exists',
		},
		listKeys: ['purchase_order'],
	},

	// PD-14690: Outstanding Vendor Issues dimension filters. All four
	// resolve through ONE combined EXISTS over the PO's open receipt
	// exceptions (multi-hop: exception → receipt_detail → po_detail → PO)
	// via the purchaseOrderIssue* family handler in typeormFilterBuilder's
	// custom case — when several dimensions are active, the SAME claim
	// must satisfy all of them ("damaged AND older than 14 days" is one
	// claim, not two different claims on one PO). Usable on any PO tab
	// (an active dimension implies has-such-open-issue) and they compose
	// with the locked purchaseOrderHasOpenIssues chip on the Outstanding
	// Vendor Issues tab.
	purchaseOrderIssueType: {
		type: 'multi-select',
		label: 'Issue Type',
		placeholder: 'Select issue types...',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'purchaseOrderIssueType',
		multiple: true,
		options: Object.entries(PurchaseOrderReceiptExceptionTypeLabels).map(
			([id, label]) => ({ id, label }),
		),
		queryMapping: {
			objectTable: 'purchase_order',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['purchase_order'],
	},

	purchaseOrderIssueStatus: {
		type: 'multi-select',
		label: 'Claim Status',
		placeholder: 'Select claim statuses...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'purchaseOrderIssueStatus',
		multiple: true,
		// Only the non-terminal statuses — resolved / written-off claims
		// are excluded by the family's base predicate and never offered.
		options: OPEN_CLAIM_STATUSES.map((id) => ({
			id,
			label: PurchaseOrderReceiptExceptionStatusLabels[id],
		})),
		queryMapping: {
			objectTable: 'purchase_order',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['purchase_order'],
	},

	purchaseOrderIssueAssignee: {
		type: 'multi-select',
		label: 'Claim Owner',
		placeholder: 'Select users...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		queryParamName: 'purchaseOrderIssueAssignee',
		apiEndpoint: '/users',
		multiple: true,
		// 'ME' resolves to the current user in the filter builder's token
		// pass; 'null' = claims nobody owns (the team's orphan queue).
		options: [
			{ id: 'ME', label: 'Me' },
			{ id: 'null', label: 'Unassigned' },
		],
		queryMapping: {
			objectTable: 'purchase_order',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['purchase_order'],
	},

	purchaseOrderIssueAge: {
		type: 'single-select',
		label: 'Issue Age',
		placeholder: 'Any age',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'purchaseOrderIssueAge',
		multiple: false,
		// Threshold buckets beat a free number input here — "anything
		// older than two weeks" is how the queue is actually worked. The
		// id is the day count the builder binds into the predicate.
		options: [
			{ id: '7', label: '7+ days old' },
			{ id: '14', label: '14+ days old' },
			{ id: '30', label: '30+ days old' },
		],
		queryMapping: {
			objectTable: 'purchase_order',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['purchase_order'],
	},

	// Approval-gate state. Drives the "Pending Approval" PO list
	// tab (filter to `submitted`) and any future per-state views.
	// Distinct from `purchaseOrderStatus` — this tracks whether the
	// PO has been routed through the threshold-driven approval gate,
	// orthogonal to the lifecycle status (QUOTE / APPROVED / etc.).
	purchaseOrderApprovalStatus: {
		...createStatusFilter({
			label: 'Approval Status',
			options: [
				{ id: 'submitted', label: 'Submitted for Approval' },
				{ id: 'approved', label: 'Approved' },
				{ id: 'rejected', label: 'Rejected' },
			],
			objectTable: 'purchase_order',
			fieldName: 'approvalStatus',
			listKeys: ['purchase_order'],
		}),
		queryParamName: 'purchaseOrderApprovalStatus',
	},
};
